import { Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import type { ImportJobResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { BulkActionBar, DataTable, RowActions } from '@trellis/roots/data'
import { CsvDropZone } from '@trellis/roots/forms'
import { PageHeader } from '@trellis/roots/navigation'
import { ConfirmDialog } from '@trellis/roots/overlays'
import { StatusBadge } from '@trellis/roots/status'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import { BookOpen, Download, ExternalLink, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  useBulkDelete,
  useDeleteImportMutation,
  useDisclosure,
  useImportJobsQuery,
  useUploadCsvMutation,
} from '@/hooks'
import { useSetBreadcrumbs } from '@/lib/breadcrumbs'
import { docsUrl } from '@/lib/docs'
import { formatDate } from '@/lib/format'
import {
  IMPORT_STATUS_LABELS,
  IMPORT_STATUS_STYLES,
  isImportJobDeletable,
  shortImportJobId,
} from '@/lib/import-status'
import { downloadTemplate } from '@/requests/data-import'
import type { TemplateType } from '@/requests/data-import'

import { IMPORT_DOCS_PATHS, IMPORT_PARENT_PATHS } from '../import.constants'
import { ImportBackLink } from '../ImportBackLink'
import { ImportPreviewSheet } from '../ImportPreviewSheet'
import type { ImportBasePath, ImportJobType, ImportPreviewConfig } from '../types'

interface ImportListProps<TRow> extends ImportPreviewConfig<TRow> {
  type: ImportJobType
  basePath: ImportBasePath
  entityLabel: string
  templateType: TemplateType
  title: string
  emptyMessage: string
}

function buildHistoryColumns({
  basePath,
  onView,
  onDelete,
}: {
  basePath: ImportBasePath
  onView: (jobId: string) => void
  onDelete: (importJob: ImportJobResponseDto) => void
}): ColumnDef<ImportJobResponseDto>[] {
  return [
    {
      id: 'shortId',
      header: 'ID',
      accessorFn: (importJob) => shortImportJobId(importJob.id),
      enableSorting: false,
    },
    { accessorKey: 'fileName', header: 'File Name' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          styles={IMPORT_STATUS_STYLES}
          labels={IMPORT_STATUS_LABELS}
        />
      ),
    },
    { accessorKey: 'totalRows', header: 'Total Rows' },
    { accessorKey: 'validRows', header: 'Valid' },
    { accessorKey: 'errorRows', header: 'Errors' },
    {
      accessorKey: 'createdAt',
      header: 'Imported',
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: 'actions',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <RowActions
          linkComponent={Link}
          onViewClick={() => onView(row.original.id)}
          triggerLabel={`Open actions menu for ${row.original.fileName}`}
          actions={[
            {
              label: 'View import job',
              icon: <ExternalLink className="mr-2 h-4 w-4" />,
              href: `${basePath}/${row.original.id}`,
            },
            ...(isImportJobDeletable(row.original.status)
              ? [
                  {
                    label: 'Delete',
                    icon: <Trash2 className="mr-2 h-4 w-4" />,
                    onClick: () => onDelete(row.original),
                    destructive: true,
                    separator: true,
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ]
}

export function ImportList<TRow>({
  type,
  basePath,
  entityLabel,
  templateType,
  title,
  emptyMessage,
  previewColumns,
  getPreviewRows,
}: ImportListProps<TRow>) {
  const navigate = useNavigate()
  useSetBreadcrumbs([
    { label: entityLabel, href: IMPORT_PARENT_PATHS[basePath] },
    { label: 'Import' },
  ])
  const uploadMutation = useUploadCsvMutation()
  const deleteMutation = useDeleteImportMutation()
  const { data: importsResult } = useImportJobsQuery()
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const previewJob = useDisclosure<string>()
  const jobToDelete = useDisclosure<ImportJobResponseDto>()

  const historyColumns = useMemo(
    () =>
      buildHistoryColumns({
        basePath,
        onView: previewJob.open,
        onDelete: jobToDelete.open,
      }),
    [basePath, previewJob.open, jobToDelete.open],
  )
  const importJobs = importsResult?.data
  const typeImports = useMemo(
    () => (importJobs ?? []).filter((importJob) => importJob.type === type),
    [importJobs, type],
  )
  const selectedJobs = typeImports.filter((importJob) => rowSelection[importJob.id])

  const {
    open: bulkDeleteOpen,
    setOpen: setBulkDeleteOpen,
    handleBulkDelete,
  } = useBulkDelete({
    selected: selectedJobs,
    getId: (importJob) => importJob.id,
    deleteFn: (id) => deleteMutation.mutateAsync(id),
    label: 'import job',
    onSuccess: clearSelection,
  })

  function handleConfirmDelete() {
    const importJob = jobToDelete.value
    if (!importJob) return
    deleteMutation.mutate(importJob.id, {
      onSuccess: () => {
        toast.success('Import job deleted')
      },
      onError: (deleteError) => {
        toast.error('Delete failed', { description: deleteError.message })
      },
    })
  }

  function clearSelection() {
    setRowSelection({})
  }

  function handleFile(file: File) {
    uploadMutation.mutate(
      { file, type },
      {
        onSuccess: (result) => {
          void navigate({ to: `${basePath}/$id`, params: { id: result.data!.id } })
        },
        onError: (uploadError) => {
          toast.error('Upload failed', { description: uploadError.message })
        },
      },
    )
  }

  return (
    <div className="p-8">
      <ImportBackLink
        to={IMPORT_PARENT_PATHS[basePath]}
        label={`Back to ${entityLabel.toLowerCase()}`}
      />
      <PageHeader
        title={title}
        subtitle={`Upload a CSV to add ${entityLabel.toLowerCase()} in bulk.`}
        actions={
          <>
            <Button variant="ghost" asChild>
              <a
                href={docsUrl(IMPORT_DOCS_PATHS[basePath])}
                target="_blank"
                rel="noopener noreferrer"
              >
                <BookOpen className="size-4" aria-hidden="true" />
                Import guide
                <ExternalLink className="size-3" aria-hidden="true" />
              </a>
            </Button>
            <Tooltip touch="none">
              <TooltipTrigger asChild>
                <Button variant="outline" onClick={() => downloadTemplate(templateType)}>
                  <Download className="size-4" aria-hidden="true" />
                  Download Template
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">
                {`A sample CSV with the columns needed to import ${entityLabel.toLowerCase()}. Fill it in and upload it below.`}
              </TooltipContent>
            </Tooltip>
          </>
        }
      />

      <div className="space-y-6">
        <CsvDropZone onFile={handleFile} disabled={uploadMutation.isPending} />

        {uploadMutation.isPending && (
          <Typography variant="body" color="muted">
            Uploading and validating…
          </Typography>
        )}

        <div className="space-y-2">
          <Typography variant="h3" as="h2">
            Recent imports
          </Typography>
          {selectedJobs.length > 0 && (
            <BulkActionBar
              selectedCount={selectedJobs.length}
              actions={[
                {
                  label: 'Delete',
                  icon: <Trash2 className="size-4" />,
                  onClick: () => setBulkDeleteOpen(true),
                  variant: 'destructive',
                },
              ]}
              onClearSelection={clearSelection}
            />
          )}
          <DataTable
            columns={historyColumns}
            data={typeImports}
            isLoading={false}
            emptyMessage={emptyMessage}
            onRowClick={(importJob) =>
              void navigate({ to: `${basePath}/$id`, params: { id: importJob.id } })
            }
            enableSelection
            isRowSelectable={(importJob) => isImportJobDeletable(importJob.status)}
            getRowId={(importJob) => importJob.id}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
          />
        </div>
      </div>

      <ImportPreviewSheet
        jobId={previewJob.value}
        basePath={basePath}
        previewColumns={previewColumns}
        getPreviewRows={getPreviewRows}
        onOpenChange={previewJob.onOpenChange}
      />

      <ConfirmDialog
        open={jobToDelete.isOpen}
        onOpenChange={jobToDelete.onOpenChange}
        title="Delete import job?"
        description={
          <>
            This permanently deletes the import job for{' '}
            <strong>{jobToDelete.value?.fileName}</strong>. No data has been imported.
          </>
        }
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selectedJobs.length} import job${selectedJobs.length === 1 ? '' : 's'}?`}
        description="This permanently deletes the selected import jobs. None of them have been imported, so no data is affected."
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
        onConfirm={() => void handleBulkDelete()}
      />
    </div>
  )
}
